import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getRetrieveWorkspaceBookQueryOptions } from '@/generated/hooks/bdk/v2/services/library';
import type {
  V1CodeManifest,
  V1CredentialInfo,
} from '@/generated/hooks/grimoire/models';
import { resolveStepConnections } from '@/modules/automation/utils/resolve-step-connections';
import type { IConnectionIcon } from './connection-icons';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { convertToDataUrl } from '@/shared/utils/image-utils';
import { getIntegrationDisplayName } from '@/shared/utils/integrations-utils';

/** A book, identified the way the library service wants it. */
interface IBookRef {
  name: string;
  version: string;
}

/** Stable empty map, so the loop below does not build one per step. */
const NO_CREDENTIALS: ReadonlyMap<string, V1CredentialInfo> = new Map();

/** Keys a book by the pair that identifies it. */
function bookKey(name: string, version: string): string {
  return `${name}@${version}`;
}

/** Params for {@link useStepConnectionIcons}. */
interface IUseStepConnectionIconsParams {
  manifest?: V1CodeManifest;
  /** The automation's connections, keyed by the alias its calls name. */
  credentialsByAlias?: ReadonlyMap<string, V1CredentialInfo>;
}

/**
 * The integrations each step reaches, with the icon to draw for each.
 *
 * Walks the same chain as the Connections tab (alias -> connection -> book ->
 * icon) on the same React Query keys, so opening one after the other costs no
 * requests. One query per distinct integration, not per step.
 *
 * A step whose book has not resolved yet, or has no icon, is left out rather
 * than given a placeholder.
 */
export function useStepConnectionIcons({
  manifest,
  credentialsByAlias,
}: IUseStepConnectionIconsParams): ReadonlyMap<number, IConnectionIcon[]> {
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  /** Which books each step reaches, and the set of books to fetch. */
  const { booksByStep, books } = useMemo(() => {
    const perStep = new Map<number, IBookRef[]>();
    const wanted = new Map<string, IBookRef>();
    for (const step of manifest?.steps ?? []) {
      if (step.number == null) continue;
      // Keyed rather than a list: two aliases can be backed by the same book,
      // and the tile would then draw that integration's badge twice.
      const refs = new Map<string, IBookRef>();
      for (const connection of resolveStepConnections(
        step.audit?.external_calls,
        credentialsByAlias ?? NO_CREDENTIALS,
      )) {
        if (!connection.book || !connection.bookVersion) continue;
        const ref = { name: connection.book, version: connection.bookVersion };
        const key = bookKey(ref.name, ref.version);
        refs.set(key, ref);
        wanted.set(key, ref);
      }
      if (refs.size > 0) perStep.set(step.number, [...refs.values()]);
    }
    return { booksByStep: perStep, books: [...wanted.values()] };
  }, [manifest, credentialsByAlias]);

  const results = useQueries({
    queries: books.map(book =>
      getRetrieveWorkspaceBookQueryOptions(
        organizationId,
        workspaceId,
        book.name,
        book.version,
      ),
    ),
  });

  // `useQueries` returns a fresh array every render, so the memo below keys on
  // when the queries last settled. Keying on the icons themselves would join a
  // few hundred kilobytes of base64 on every render of the editor.
  const settled = results.map(result => result.dataUpdatedAt).join('|');
  const data = results.map(result => result.data);

  /** Every resolved book's icon, keyed the way the steps reference it. */
  const iconsByBook = useMemo(() => {
    const icons = new Map<string, IConnectionIcon>();
    data.forEach((result, index) => {
      const book = result?.data?.book;
      const icon = convertToDataUrl(book?.icon);
      if (!book || !icon) return;
      icons.set(bookKey(books[index].name, books[index].version), {
        icon,
        name: getIntegrationDisplayName(book),
      });
    });
    return icons;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reason: keyed on `settled`
  }, [books, settled]);

  return useMemo(() => {
    const byStep = new Map<number, IConnectionIcon[]>();
    for (const [step, refs] of booksByStep) {
      const icons = refs
        .map(ref => iconsByBook.get(bookKey(ref.name, ref.version)))
        .filter((icon): icon is IConnectionIcon => icon !== undefined);
      if (icons.length > 0) byStep.set(step, icons);
    }
    return byStep;
  }, [booksByStep, iconsByBook]);
}
